import React, { useCallback, useEffect, useState } from 'react';
import { Text, Overlay, LoadingBalls } from '@moda/om';
import {
  CartUpdateAction,
  CartUpdatePropertyName,
  useCart,
  useCartItems,
  CartVersion,
  useUpdateCartMutation
} from '../../hooks/useCart';
import { tc } from '../../lib/trackingContext';
import { reportError } from '../../lib/reportError';
import { useErrorModal } from '../../hooks/useErrorModal';
import { useCheckoutFormStates } from '../CheckoutFormStates';
import { CheckoutSection } from '../CheckoutSection';
import { ShippingMethodForm } from './ShippingMethodForm';
import { ShippingMethodPreview } from './ShippingMethodPreview';

enum Mode {
  Resting,
  Editing,
  Saving
}

export const ShippingMethod: React.FC = () => {
  const { cart } = useCart();
  const { availableNowItems } = useCartItems();
  const [updateCart] = useUpdateCartMutation();
  const { setFormState } = useCheckoutFormStates();
  const { showErrorModal } = useErrorModal();

  const { selectedShippingMethodId, shippingMethods } = cart.attributes;

  const selectedShippingMethod = shippingMethods?.find(
    method => method.method === selectedShippingMethodId
  );
  const onlyOneShippingMethod = shippingMethods?.length === 1;

  const [mode, setMode] = useState(onlyOneShippingMethod ? Mode.Resting : Mode.Editing);

  const [selectedOptionId, setSelectedOptionId] = useState(selectedShippingMethodId);

  const selectedOption = shippingMethods?.find(method => method.method === selectedOptionId);

  const onSubmit = useCallback(async () => {
    setMode(Mode.Saving);

    try {
      await updateCart(CartUpdateAction.ShippingMethod, {
        [CartUpdatePropertyName.ShippingMethod]: selectedOptionId ?? undefined
      });
      setMode(Mode.Resting);
    } catch (error) {
      setMode(Mode.Editing);
      showErrorModal();
      reportError('SHIPPING_METHOD_SUBMIT', error);
    }
  }, [updateCart, selectedOptionId, showErrorModal]);

  const trackShippingMethod = useCallback(() => {
    if (selectedOption) {
      availableNowItems.forEach(lineItem =>
        tc.track('Product Delivery Timing Shown', {
          addToScopeOnce: { lineItem },
          addToTrackingEvent: { label: selectedOption.transitTime }
        })
      );
    }
  }, [availableNowItems, selectedOption]);

  useEffect(() => {
    setFormState('shippingMethod', mode === Mode.Resting ? 'closed' : 'open');
  }, [setFormState, mode]);

  if (cart.attributes.version !== CartVersion.Checkout) return null;

  if (shippingMethods == null || selectedOptionId == null) return null;

  return (
    <CheckoutSection title="Shipping Method">
      {(mode === Mode.Editing || mode === Mode.Saving) && (
        <ShippingMethodForm
          shippingOptions={shippingMethods}
          selectedOption={selectedOptionId}
          onSelectOption={setSelectedOptionId}
          onCancel={() => setMode(Mode.Resting)}
          onSubmit={() => {
            onSubmit();
            trackShippingMethod();
          }}
        />
      )}
      {mode === Mode.Resting && (
        <ShippingMethodPreview
          option={selectedShippingMethod}
          onChange={shippingMethods.length > 1 ? () => setMode(Mode.Editing) : undefined}
        />
      )}
      <Overlay show={mode === Mode.Saving}>
        <LoadingBalls />

        <Text treatment="eyebrow">Saving</Text>
      </Overlay>
    </CheckoutSection>
  );
};
